import { onMount } from 'svelte';

/**
 * Reactive wrapper for the "load on mount, track loading + error" pattern
 * that recurred across nearly every route fetcher (DonationsTab, StatsTab,
 * Public profile, etc.). Cuts the boilerplate
 *
 *     let data = $state(null);
 *     let loading = $state(true);
 *     let error = $state(false);
 *     onMount(async () => {
 *       try { data = await fetcher(); } catch { error = true; }
 *       finally { loading = false; }
 *     });
 *
 * down to:
 *
 *     const view = new AsyncData(() => fetchThing());
 *     // template: {#if view.loading} … {:else if view.error} … {:else} …
 *     // refetch:  view.reload()
 *
 * The class lives in a `.svelte.ts` file so Svelte 5 runes work in the
 * field defaults; consumers import it from any component.
 */
export class AsyncData<T> {
  data = $state<T | null>(null);
  loading = $state(true);
  error = $state(false);

  constructor(
    private readonly loader: () => Promise<T>,
    options: { autoLoad?: boolean } = {},
  ) {
    if (options.autoLoad !== false) {
      onMount(() => {
        void this.reload();
      });
    }
  }

  /** Re-run the loader and replace `data` on success. Errors flip
   *  `error` to true; the previous `data` is preserved so a refresh
   *  failure doesn't blank the UI mid-view. */
  async reload(): Promise<void> {
    this.loading = true;
    this.error = false;
    try {
      this.data = await this.loader();
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }
}
